import React, { useCallback } from 'react';

import { type UseQueryLightError } from '@atlaskit/teams-client';
import {
	AnalyticsEventActions,
	AnalyticsEventSource,
	DEFAULT_TIMEOUT_USER_EXP,
	ExperienceSuccess,
	useAnalytics,
	useExpTrackerStart,
	useExpTrackerStop,
	UserExperienceNames,
} from '@atlassian/ptc-common';

import { isRealErrorUserProfile } from '../../utils/error';
import EmptyWorkState, { ErrorWorkState } from '../EmptyWorkState';
import WorkActivityList from '../WorkActivityList';
import { type ButtonLocation } from '../WorkList/WorkList';

import { type ProfileWorkListProps } from './types';

export function ProfileWorkList(props: ProfileWorkListProps) {
	const { activities, userId, currentUserId, isLoading, userFullName, hasError, error } = props;

	const analytics = useAnalytics();

	const onClickViewAllLink = useCallback(
		(location: ButtonLocation) => {
			analytics.pushUIEvent({
				source: AnalyticsEventSource.USER_PROFILE_SCREEN,
				actionSubject: 'viewAllWork',
				action: AnalyticsEventActions.CLICKED,
				attributes: {
					location,
				},
			});
		},
		[analytics],
	);

	const onClickShowMore = useCallback(
		(numItemsDisplayed: number) => {
			analytics.pushUIEvent({
				source: AnalyticsEventSource.USER_PROFILE_SCREEN,
				actionSubject: 'moreWork',
				action: AnalyticsEventActions.CLICKED,
				attributes: {
					numItemsDisplayed,
				},
			});
		},
		[analytics],
	);

	const onActivityEntryClick = useCallback(
		(provider: string, entryIndex: number) => {
			analytics.pushUIEvent({
				source: AnalyticsEventSource.USER_PROFILE_SCREEN,
				actionSubject: 'activityEntry',
				action: AnalyticsEventActions.CLICKED,
				attributes: {
					provider: provider.toUpperCase(),
					entryIndex,
				},
			});
		},
		[analytics],
	);

	useExpTrackerStart({
		id: userId,
		name: UserExperienceNames.USER_PROFILE_WORKED_ON,
		timeout: DEFAULT_TIMEOUT_USER_EXP,
	});

	useExpTrackerStop({
		name: UserExperienceNames.USER_PROFILE_WORKED_ON,
		error,
		checkShouldAbort: (error) => {
			if (isRealErrorUserProfile(error as unknown as UseQueryLightError)) {
				return;
			}

			return `Aborted because of ${error.message}`;
		},
	});

	let emptyView;
	if (hasError) {
		emptyView = <ErrorWorkState userFullName={userFullName} userId={userId} />;
	} else {
		emptyView = (
			<EmptyWorkState
				isCurrentUser={!!userId && userId === currentUserId}
				userFullName={userFullName}
				isForTable={false}
			/>
		);
	}

	return (
		<>
			<WorkActivityList
				activities={activities}
				currentUserId={currentUserId}
				viewAllLink={`${userId}/work`}
				isLoading={isLoading}
				onClickViewAllLink={onClickViewAllLink}
				onClickShowMore={onClickShowMore}
				onClickActivityEntry={onActivityEntryClick}
				emptyView={emptyView}
				userId={userId}
			/>
			<ExperienceSuccess
				hasLoaded={!hasError && !isLoading}
				name={UserExperienceNames.USER_PROFILE_WORKED_ON}
			/>
		</>
	);
}

export default ProfileWorkList;
